// askSmart-ui/src/pages/TOSP/tenancyTransfer.ts
import { assignedIndices } from "./components/LeaseReview/leaseSource";
import { Tenant } from "./types";

// Moving a tenancy from one person to another.
//
// Two records say who a tenancy belongs to, and they have to move together:
//
//   the TOSP tenant record   — primaryContactEmail, which grants the portal,
//                              the sales routes and the lease lookup
//   the SMRT Docs assignment — which service the person can open
//
// Leaving either behind produces a half-transfer that looks fine on one screen
// and fails on the other: a tenant who can sign in but whose lease won't open,
// or a previous holder who still has the lease of a business they no longer run.
//
// What deliberately does NOT move here:
//
//   reported sales    — keyed by tenantId, never by user, so they stay with the
//                       business where they belong
//   signed documents  — they sit on the outgoing person's own state record and
//                       are their execution record. Whether they should follow
//                       depends on WHY the tenancy is moving, so it's asked
//                       separately rather than assumed.

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export interface OrgUser {
  userId: string;
  name: string;
  email: string;
  /** From the account, when it has one — carried onto the tenancy on transfer. */
  phone: string;
}

const getJson = async (path: string): Promise<any> => {
  const res = await fetch(`${BACKEND}${path}`, { credentials: "include" });
  if (!res.ok) throw new Error(`${path} returned ${res.status}`);
  return res.json();
};

/** Everyone in the org who could hold a tenancy, in name order. */
export async function fetchOrgUsers(): Promise<OrgUser[]> {
  const data = await getJson("/approved_users");
  const users: any[] = data.users || data || [];
  return users
    .map((user) => ({
      userId: String(user.user_id || user.id || ""),
      name: String(user.name || "").trim(),
      email: String(user.email || "").trim(),
      phone: String(user.phone || "").trim(),
    }))
    .filter((user) => user.userId && user.email)
    .sort((a, b) => (a.name || a.email).localeCompare(b.name || b.email));
}

export interface TransferOutcome {
  /** False when the tenancy names no service, so there was nothing to move. */
  serviceMoved: boolean;
  /** How many documents followed, when the caller asked for them. */
  documentsMoved: number;
}

/**
 * Moves one service's documents from the outgoing holder to the incoming one.
 *
 * Separate from the assignment because whether documents follow depends on WHY
 * the tenancy is moving. A property manager handing over a tenancy she set up
 * was only ever holding the tenant's own lease; a business changing hands
 * leaves the outgoing party's executed lease where it belongs.
 *
 * Returns how many were moved. Zero is a legitimate answer — a tenancy whose
 * lease was never filed has nothing to hand over — and is reported rather than
 * dressed up as success.
 */
async function moveServiceDocuments(
  fromUserId: string,
  toUserId: string,
  serviceId: string,
): Promise<number> {
  const res = await fetch(`${BACKEND}/smrtdocs/transfer-service`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ fromUserId, toUserId, serviceId }),
  });

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(
      body?.error ||
        (res.status === 403
          ? "You don't have permission to move SMRT Docs documents."
          : `Couldn't move the documents (${res.status}).`),
    );
  }

  const body = await res.json();
  return body?.moved ? Number(body.documents) || 0 : 0;
}

/**
 * Hands a tenancy to another person.
 *
 * The SMRT Docs assignment moves first. If it fails, the tenant record is left
 * untouched and the transfer simply hasn't happened — recoverable, and honest.
 * Were it the other way round, a failure would leave the incoming holder as the
 * contact on a tenancy whose lease they can't open, which reads as a working
 * transfer until somebody tries to use it.
 */
export async function moveTenancyContact(
  tenancy: Tenant,
  incoming: OrgUser,
  saveTenant: (tenant: Tenant) => Promise<void>,
  options: {
    /** Whoever holds it now — needed to know whose record the papers are on. */
    outgoing: OrgUser | null;
    /** Whether the lease and documents follow the tenancy. */
    withDocuments: boolean;
    /** Who is performing it. Recorded on the tenancy, not inferred later. */
    byEmail: string;
    byName: string;
  },
): Promise<TransferOutcome> {
  const at = new Date().toISOString();
  const serviceId = (tenancy.smrtDocsServiceId || "").trim();
  let serviceMoved = false;
  let documentsMoved = 0;

  // Before anything is reassigned, while the outgoing holder is still
  // discoverable from the record. Afterwards nothing points back to them.
  if (options.withDocuments && serviceId) {
    if (!options.outgoing) {
      throw new Error(
        "The current holder has no matching account, so their documents can't " +
          "be found. Transfer without documents, or fix the contact first.",
      );
    }
    documentsMoved = await moveServiceDocuments(
      options.outgoing.userId,
      incoming.userId,
      serviceId,
    );
  }

  if (serviceId) {
    const [serviceData, assignmentData] = await Promise.all([
      getJson("/smrtdocs/services"),
      getJson("/smrtdocs/assignments"),
    ]);

    const services: any[] = serviceData.services || [];
    // Assignments name a service by its POSITION, so the id has to be resolved
    // to one. A service that has since been deleted resolves to nothing, and
    // the caller is told rather than having an unrelated service moved.
    const index = services.findIndex((service) => service?.id === serviceId);
    if (index < 0) {
      throw new Error(
        "This tenancy's lease is on a SMRT Docs service that no longer exists. " +
          "Re-review the lease before transferring it.",
      );
    }

    const assignments: Record<string, any> = assignmentData.assignments || {};
    const next: Record<string, any> = { ...assignments };

    // Taken from everyone who holds it — not only the recorded contact. If the
    // record and the assignments have drifted apart, a transfer is the moment
    // to bring them back together rather than to preserve the discrepancy.
    for (const userId of Object.keys(next)) {
      if (userId === incoming.userId) continue;
      const held = assignedIndices(next[userId]).filter((i) => i !== index);
      next[userId] = { mode: "multi", values: held };
    }

    const theirs = assignedIndices(next[incoming.userId]);
    next[incoming.userId] = {
      mode: "multi",
      values: theirs.includes(index) ? theirs : [...theirs, index],
    };

    const res = await fetch(`${BACKEND}/smrtdocs/assignments`, {
      method: "PUT",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ assignments: next }),
    });
    if (!res.ok) {
      throw new Error(
        res.status === 403
          ? "You don't have permission to change SMRT Docs assignments."
          : `Couldn't move the lease (${res.status}).`,
      );
    }
    serviceMoved = true;
  }

  await saveTenant({
    ...tenancy,
    // All three identify the NEW holder and all three come from their account.
    // None of them ever falls back to the outgoing contact: a tenancy carrying
    // the new person's email beside the old person's name or number is worse
    // than one carrying a blank, because it reads as correct and nobody goes
    // to fix it.
    //
    // The name falls back to the incoming EMAIL rather than the outgoing name,
    // so the row still identifies somebody real when an account has no name on
    // it. Phone is left blank when the account has none, for the admin to fill.
    primaryContactName: incoming.name || incoming.email,
    primaryContactEmail: incoming.email,
    primaryContactPhone: incoming.phone || "",
    // Written last, from the value being replaced — so a tenancy always says
    // where it came from even when the documents were left behind.
    previousContactEmail: tenancy.primaryContactEmail || "",
    contactTransferredAt: at,
    // Appended, never replaced. A tenancy handed on twice has been handed on
    // twice, and the earlier move is the one most likely to be asked about.
    transferLog: [
      ...(tenancy.transferLog || []),
      {
        at,
        byEmail: options.byEmail,
        byName: options.byName,
        fromEmail: tenancy.primaryContactEmail || "",
        toEmail: incoming.email,
        toName: incoming.name,
        serviceMoved,
        documentsMoved,
      },
    ],
  });

  return { serviceMoved, documentsMoved };
}
